// [목적] /explore 필터 모달 — 여행 기간 select + 테마 멀티셀렉트(제어 컴포넌트, URL 쿼리 SoT)
// [이유] M7 P1(발견 화면 서버 검색·필터) — ExplorePage(데스크톱)의 기존 비제어 FilterModal(로컬 state,
//        "결과 보기"를 눌러도 실제 필터에 반영되지 않던 mock)을 useExploreFilters 훅과 연결되는 제어
//        컴포넌트로 교체. ExploreMobile 도 동일 컴포넌트를 재사용(중복 정의 회피, 필터 모달 1벌 원칙).
// [사이드 이팩트] 선택 즉시 onDurationChange/onTagsToggle 콜백으로 URL 쿼리(useExploreFilters)에 반영 —
//               "결과 보기" 버튼은 모달을 닫기만 한다(적용은 이미 실시간으로 끝난 상태).
// [수정 시 고려사항] THEME_OPTIONS 는 listFoliosQuerySchema.tags(vibe 배열 overlap 매칭) 값과 1:1 —
//                   카테고리 칩 10종(FILTERS)도 같은 URL 상태(tags/duration)를 갱신한다 — 활성 칩은 URL 에서
//                   파생되므로 모달과 상태 충돌 없음(useExploreFilters CHIP_FILTERS, 2026-09-17).

import { Box, ButtonBase, Modal, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import type { DurationRangeLabel } from '../../hooks/useExploreFilters.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];

export const DURATION_OPTIONS: ReadonlyArray<DurationRangeLabel> = [
  '전체',
  '당일',
  '1박 2일',
  '2박 3일+',
];

/** listFoliosQuerySchema.tags(vibe 배열 overlap) 매칭 값 — 폴리오 카드에 노출되는 #태그 문구와 동일 */
export const THEME_OPTIONS = [
  '바다',
  '카페',
  '캠핑',
  '트레킹',
  '서핑',
  '반려견',
  '감성',
  '액티비티',
];

export interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  duration: DurationRangeLabel;
  onDurationChange: (duration: DurationRangeLabel) => void;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  onReset: () => void;
  resultCount: number;
}

/**
 * [목적] 필터 모달 — 여행 기간 select + 테마 멀티셀렉트 + 초기화/결과 보기(제어 컴포넌트)
 * [이유] M7 P1: URL 쿼리(useExploreFilters)를 SoT 로 즉시 반영 — 모달을 닫아도 필터 유지.
 * [사이드 이팩트] 없음 — 모든 상태는 호출부(useExploreFilters)가 소유.
 * [수정 시 고려사항] Esc 키로 닫힘(MUI Modal 기본 동작). Tab 순서 내부 격리(Modal autoFocus).
 */
export function FilterModal({
  open,
  onClose,
  duration,
  onDurationChange,
  tags,
  onTagsChange,
  onReset,
  resultCount,
}: FilterModalProps): JSX.Element {
  function toggleTheme(t: string): void {
    onTagsChange(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label="폴리오 필터"
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: '16px' }}
    >
      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '18px',
          width: '100%',
          maxWidth: 440,
          p: '28px 24px 20px',
          boxShadow: tokens.shadow.modal,
          outline: 'none',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="필터 설정"
      >
        {/* 헤더 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: '22px',
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK_HI }}>필터</Typography>
          <ButtonBase
            onClick={onClose}
            aria-label="필터 닫기"
            sx={{
              color: INK_LOW,
              fontSize: 18,
              width: 32,
              height: 32,
              borderRadius: '50%',
              '&:hover': { bgcolor: PAPER2 },
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            ×
          </ButtonBase>
        </Box>

        {/* 여행 기간 */}
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK_HI, mb: '10px' }}>
          여행 기간
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mb: '22px' }}>
          {DURATION_OPTIONS.map((d) => {
            const active = duration === d;
            return (
              <ButtonBase
                key={d}
                onClick={() => onDurationChange(d)}
                aria-label={`여행 기간 ${d}`}
                aria-pressed={active}
                sx={{
                  bgcolor: active ? INK_HI : '#fff',
                  color: active ? '#fff' : INK_MID,
                  border: `1px solid ${active ? INK_HI : BORDER}`,
                  borderRadius: 99,
                  px: '14px',
                  py: '7px',
                  fontSize: 12,
                  fontWeight: 600,
                  transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                {d}
              </ButtonBase>
            );
          })}
        </Box>

        {/* 테마 */}
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: INK_HI, mb: '10px' }}>
          테마
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mb: '28px' }}>
          {THEME_OPTIONS.map((t) => {
            const active = tags.includes(t);
            return (
              <ButtonBase
                key={t}
                onClick={() => toggleTheme(t)}
                aria-label={`테마 ${t}`}
                aria-pressed={active}
                sx={{
                  bgcolor: active ? BRAND : '#fff',
                  color: active ? '#fff' : INK_MID,
                  border: `1px solid ${active ? BRAND : BORDER}`,
                  borderRadius: 99,
                  px: '13px',
                  py: '6px',
                  fontSize: 11.5,
                  fontWeight: 600,
                  transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                  '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                }}
              >
                {t}
              </ButtonBase>
            );
          })}
        </Box>

        {/* 액션 버튼 */}
        <Box sx={{ display: 'flex', gap: '10px' }}>
          <ButtonBase
            onClick={onReset}
            aria-label="필터 초기화"
            sx={{
              flex: 1,
              bgcolor: PAPER1,
              color: INK_MID,
              border: `1px solid ${BORDER}`,
              borderRadius: `${tokens.radius.m}px`,
              py: '11px',
              fontSize: 12.5,
              fontWeight: 600,
              '&:hover': { bgcolor: PAPER2 },
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            초기화
          </ButtonBase>
          <ButtonBase
            onClick={onClose}
            aria-label={`결과 ${resultCount}개 보기`}
            sx={{
              flex: 2,
              bgcolor: BRAND,
              color: '#fff',
              borderRadius: `${tokens.radius.m}px`,
              py: '11px',
              fontSize: 12.5,
              fontWeight: 700,
              '&:hover': { opacity: 0.88 },
              '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
            }}
          >
            결과 {resultCount}개 보기
          </ButtonBase>
        </Box>
      </Box>
    </Modal>
  );
}
